/**
 * Specialized hook for OrgEmployee operations
 *
 * @module @shared/services/hooks/useOrgEmployeeApi
 */

import { useCallback } from 'react';
import { useApiCore } from './useApiCore';
import type { OrgEmployee, RestList, Employee } from '@shared/types';
import type { ApiResult } from '../apiService';

/**
 * Hook that provides OrgEmployee-specific API methods with automatic authentication.
 *
 * @returns OrgEmployee-specific methods
 *
 * @example
 * ```typescript
 * const { get, getEmployees } = useOrgEmployeeApi();
 *
 * const oe = await get();
 * const employees = await getEmployees();
 * ```
 */
export function useOrgEmployeeApi(): {
    get: () => ApiResult<OrgEmployee>;
    getEmployees: () => ApiResult<RestList<Employee>>;
} {
    const { getToken, apiService } = useApiCore();

    return {
        get: useCallback(async (): ApiResult<OrgEmployee> => {
            const token = await getToken();
            return apiService.orgEmployee.get(token);
        }, [getToken, apiService]),

        getEmployees: useCallback(async (): ApiResult<RestList<Employee>> => {
            const token = await getToken();
            return apiService.orgEmployee.getEmployees(token);
        }, [getToken, apiService]),
    };
}
